<template>
    <base-box padding="none">
        <template slot="content">
            <g-media-object class="with-padding" left-margin="0" :class="{'out-of-stock': isFaded}">
                <template slot="left">
                    <div class="product-image margin-right-normal">
                        <div v-if="isFaded" class="out-of-stock-mask"></div>
                        <img :src="imageUrl" alt="product image"/>
                    </div>
                </template>
                <template>
                    <div class="media-object-margin-left-4">
                        <div class="margin-bottom-small">
                            <g-typography tag="p" :value="product.name"/>
                        </div>
                        <div>
                            <g-typography tag="h5" color="secondary">
                                {{ productPrice }}
                            </g-typography>
                        </div>
                    </div>
                </template>
            </g-media-object>
            <template v-if="isFooter">
                <g-divider margin="none"/>
                <div class="with-padding product-card-footer" :class="{'out-of-stock': isFaded}">
                    <g-level>
                        <template v-if="shouldSelectOptions" slot="center">
                            <g-button
                                type="link"
                                @click="$emit('edit')"
                            >+ Select Options
                            </g-button>
                        </template>
                        <template v-else>
                            <div class="product-info">
                                <slot name="info">
                                    <g-typography v-if="stocked" tag="span" color="secondary" :value="productInfo"/>
                                    <g-typography v-else tag="span" color="error" value="Out of stock"/>
                                </slot>
                            </div>
                        </template>
                        <template slot="right">
                            <g-button
                                v-if="canEdit"
                                icon="pen"
                                :minimalist="true"
                                @click="() => $emit('edit', selectedVariant.id)"
                                class="pull-right"
                            >
                                Edit
                            </g-button>
                            <g-button
                                v-if="canRemove"
                                icon="trash-alt"
                                :minimalist="true"
                                @click="$emit('remove')"
                                class="pull-right"
                            >
                                Remove
                            </g-button>
                        </template>
                    </g-level>
                </div>
            </template>
        </template>
    </base-box>
</template>

<script>
    import {PLACEHOLDER_IMAGE} from "../../../../constants/cdn-assets";
    import * as _ from "lodash";
    import {formatCurrency} from "../../../../helpers/formatters";
    import ProductsMixin from "../mixins/ProductsMixin";

    export default {
        name: "SelectedProductCard",
        mixins: [
            ProductsMixin,
        ],
        props: {
            product: {
                type: Object,
            },
            campaign: {
                type: Object,
            },
            preselectedProduct: {
                type: Object,
            }
        },
        computed: {
            isFaded() {
                return !this.stocked && !this.canSelectOptions;
            },
            isFooter() {
                return this.canSelectOptions
                    || this.canEdit
                    || this.canRemove
                    || !this.stocked
                    || this.productInfo
            },
            imageUrl() {
                return this.product.image || PLACEHOLDER_IMAGE;
            },
            showPrices() {
                return _.get(this.campaign, 'compensation_settings.show_prices', true);
            },
            productPrice() {
                const currency = _.get(this.campaign, 'brand.currency', '');

                if (this.showPrices) {
                    const variantPrice = _.get(this.product, 'variant.price');
                    const productPrice = _.get(this.product, 'price');
                    return formatCurrency(variantPrice || productPrice, currency);
                }

                return '';
            },
            canEdit() {
                return !!((this.hasVariants && !this.preselectedItem) || (this.preselectedItem && this.canSelectOptions && !this.shouldSelectOptions));
            },
            canRemove() {
                return !this.preselectedItem;
            },
            hasVariants() {
                return !!(this.product.variants && this.product.variants.length > 0);
            },
            canSelectOptions() {
                return !!(this.preselectedItem && this.preselectedItem.select_options);
            },
            shouldSelectOptions() {
                return this.canSelectOptions && !this.hasSelectedVariant;
            },
            selectedVariant() {
                return this.product.variant;
            },
            optionsValues() {
                return _.get(this.product, 'variant.option_values');
            },
            hasSelectedVariant() {
                return !!this.selectedVariant;
            },
            stocked() {
                if (!this.product.variant) {
                    return !!this.product.is_in_stock;
                }

                return this.product.variant.is_in_stock !== false;
            },
            preselectedItem() {
                return (_.get(this, 'campaign.compensation_settings.select_products', true) ? null : this.preselectedProduct);
            }
        },
    }
</script>

<style lang="scss" scoped>
    .with-padding {
        padding: $g-spacing-4;
    }

    .margin-bottom-small {
        margin-bottom: $g-spacing-3;
    }

    .margin-right-normal {
        margin-right: $g-spacing-5;
    }

    .product-image {
        width: 66px;
        height: 66px;
        border-radius: $g-border-radius-md;
        border: 1px solid $g-color-grey-4;
        overflow: auto;
        display: flex;
        justify-content: center;
        align-items: center;
        position: relative;

        & img {
            max-width: 100%;
            max-height: 100%;
        }

        & .out-of-stock-mask {
            background-color: rgba(249, 250, 251, 0.5);
            position: absolute;
            top: 0;
            left: 0;
            width: 100%;
            height: 100%;
        }
    }

    .product-card-footer {
        & .product-info {
            display: flex;
            align-items: center;
            height: 32px;
            width: 100%;

            & span {
                overflow: hidden;
                white-space: nowrap;
                text-overflow: ellipsis;
            }
        }

        & .limited-width {
            display: inline-block;
            max-width: 250px;
        }

        & button {
            width: 5rem;
        }
    }

    .out-of-stock {
        background-color: $g-color-grey-2;
    }
</style>
